/**
 * Per-frame style isolation.
 *
 * Every frame's HTML is injected into the shared document via
 * dangerouslySetInnerHTML, so a bare `<style>` block (agent-, template-, or
 * paste-authored) would leak globally and restyle the whole app + sibling
 * frames. We wrap each block's rules in a CSS `@scope` whose implicit root is
 * the <style>'s parent (the frame's content root), confining them to that one
 * frame. `:root` custom-property definitions are remapped to `:scope` so they
 * don't leak to the document root.
 *
 * Chromium-only (@scope, Chrome 118+) — matches the drawElement target.
 */

/** Neutralize CSS that could escape the frame or leak/track externally:
 *  <style>/<script> breakout, @import, viewport-escaping position, and
 *  external url() resources. data: URLs (inline images) are kept. */
export function hardenCss(css: string): string {
  return css
    .replace(/<\/?\s*(style|script)[^>]*>?/gi, '')
    .replace(/@import[^;]*;?/gi, '')
    .replace(/position\s*:\s*(fixed|sticky)/gi, 'position:relative')
    .replace(/url\(\s*['"]?\s*(?:https?:)?\/\/[^)]*\)/gi, 'none');
}

/** Wrap one block of CSS so it only applies inside the frame's subtree. */
export function scopeCssBlock(css: string): string {
  return `@scope {\n${hardenCss(css).replace(/:root\b/g, ':scope')}\n}`;
}

/**
 * Rewrite every <style> block in `html` to be frame-scoped. Idempotent: a
 * block that already contains `@scope` (e.g. paste output) is left as-is.
 * No-op when there is no <style> or no DOM (SSR/tests without document).
 */
export function isolateStyles(html: string): string {
  if (typeof document === 'undefined' || !html.includes('<style')) return html;
  const tmpl = document.createElement('template');
  tmpl.innerHTML = html;
  const host = document.createElement('div');
  host.appendChild(tmpl.content);

  let changed = false;
  for (const styleEl of Array.from(host.querySelectorAll('style'))) {
    const css = styleEl.textContent ?? '';
    if (!css.trim() || css.includes('@scope')) continue; // already scoped / empty
    styleEl.textContent = scopeCssBlock(css);
    changed = true;
  }
  return changed ? host.innerHTML : html;
}
